
import React, { useState } from 'react';
import { Link as LinkIcon, Edit, Check, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

interface ProjectItemProps {
  id?: string;
  title: string;
  description?: string | null;
  project_url?: string | null;
  skills?: string[];
  onUpdate?: (id: string, updatedProject: any) => void;
}

const ProjectItem: React.FC<ProjectItemProps> = ({ 
  id, 
  title, 
  description, 
  project_url, 
  skills,
  onUpdate 
}) => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editedProject, setEditedProject] = useState({
    title,
    description: description || '',
    project_url: project_url || ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedProject(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!id) return;

    try {
      const { error } = await supabase
        .from('job_seeker_projects')
        .update({
          title: editedProject.title,
          description: editedProject.description,
          project_url: editedProject.project_url
        })
        .eq('id', id);

      if (error) throw error;

      setIsEditing(false);
      
      if (onUpdate) {
        onUpdate(id, editedProject);
      }

      toast({
        title: "Project Updated",
        description: "Your project has been successfully updated."
      });
    } catch (error) {
      console.error('Error updating project:', error);
      toast({
        title: "Update Failed",
        description: "There was an error updating your project.",
        variant: "destructive"
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedProject({
      title,
      description: description || '',
      project_url: project_url || ''
    });
  };

  if (isEditing) {
    return (
      <div className="border-b pb-2 mb-2 space-y-2">
        <Input
          name="title"
          value={editedProject.title}
          onChange={handleInputChange}
          className="font-medium"
          placeholder="Project title"
        />
        <Textarea
          name="description"
          value={editedProject.description}
          onChange={handleInputChange}
          className="text-sm"
          placeholder="Project description"
        />
        <Input
          name="project_url"
          value={editedProject.project_url}
          onChange={handleInputChange}
          className="text-xs"
          placeholder="Project URL"
        />
        {skills && skills.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {skills.map((skill, index) => (
              <Badge key={index} variant="outline" className="text-xs py-0 h-5 bg-[#0EA5E9]/10">
                {skill}
              </Badge>
            ))}
          </div>
        )}
        <div className="flex space-x-2 mt-2">
          <button 
            onClick={handleSave}
            className="flex items-center text-xs text-green-600"
          >
            <Check className="h-3 w-3 mr-1" /> Save
          </button>
          <button 
            onClick={handleCancel}
            className="flex items-center text-xs text-red-600"
          >
            <X className="h-3 w-3 mr-1" /> Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b pb-2 mb-2">
      <div className="flex justify-between items-start">
        <h4 className="text-sm font-medium">{title}</h4>
        {onUpdate && (
          <button 
            onClick={() => setIsEditing(true)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            <Edit className="h-3 w-3" />
          </button>
        )}
      </div>
      {description && (
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      )}
      {project_url && (
        <a 
          href={project_url} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-xs text-blue-600 flex items-center mt-1"
        >
          <LinkIcon className="h-3 w-3 mr-1" /> View Project
        </a>
      )}
      {skills && skills.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {skills.map((skill, index) => (
            <Badge key={index} variant="outline" className="text-xs py-0 h-5 bg-[#0EA5E9]/10">
              {skill}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectItem;
